/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Warning } from '@mui/icons-material';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import Disclaimer from '../../../common/layout/header/Disclaimer';
import { InstallationType } from '../../../../shared/entities/terminal';
import { RootState } from '../../../../store';
import * as TerminalHistoryActions from '../../../../store/terminalHistory/actions';
import {
  getOtherTerminalOnAircraft,
  getSelectedTerminalTypeLabelBasedOnTab,
} from '../../../../utils/TerminalUtilities';
import { chartContent } from './ChartItems';
import ChartsList from './ChartsList';
import ChartsContainerWrapper from './ChartsContainerWrapper';
import ChartsSeriesColorWrapper from './ChartsSeriesColorWrapper';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../../common/map/GoogleMapSingleton';
import AllProtocolsModal from '../Protocols/AllProtocolsModal';

interface ContainerStateProps extends StateProps, DispatchProps {
  leftMargin: number;
  isExportView: boolean;
  supportsFtv: boolean;
}

function ChartsContainer({
  isRequestingTerminalHistory,
  printMode,
  requestTerminalHistoryError,
  mapSettingsFtvMapHeight,
  leftMargin,
  isExportView,
  supportsFtv,
  getTerminalHistoryOnce,
  terminalDetails,
  selectedTerminalTabIndex,
}: ContainerStateProps) {
  const [chartList, setChartList] = useState({ ...chartContent });
  // NOTE: the url does not change between tab changed for a dual antenna and Ku + Ka
  const { datapathUuid } = useParams();

  const otherDatapathUuid = getOtherTerminalOnAircraft(terminalDetails).datapathUuid;
  const selectedTerminalType = getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminalDetails);
  const isDualTerminal = (terminalDetails || {}).installationType === InstallationType.DUAL_TERMINAL;
  const terminalTypeLabel = (terminalDetails || {}).terminalTypeLabel;
  const terminalDetailsLoaded = !isNil(terminalDetails) && terminalDetails.datapathUuid === datapathUuid;

  const fetchChartData = useCallback(
    (datapathUuid: string) => {
      getTerminalHistoryOnce(datapathUuid);
    },
    [getTerminalHistoryOnce],
  );

  // get terminal history and traffic data for non-dual antenna terminals
  useEffect(() => {
    if (terminalDetailsLoaded && !isDualTerminal) {
      fetchChartData(datapathUuid);
    }
  }, [terminalDetailsLoaded, fetchChartData, datapathUuid, isDualTerminal]);

  // fetch fresh terminal history when the dual antenna tab switches
  useEffect(() => {
    if (terminalDetailsLoaded && isDualTerminal) {
      const selectedDatapathUuid = selectedTerminalType === terminalTypeLabel ? datapathUuid : otherDatapathUuid;
      fetchChartData(selectedDatapathUuid);
    }
  }, [
    terminalDetailsLoaded,
    isDualTerminal,
    fetchChartData,
    datapathUuid,
    otherDatapathUuid,
    terminalTypeLabel,
    selectedTerminalType,
  ]);

  return (
    <ChartsContainerWrapper printMode={printMode} mapHeight={mapSettingsFtvMapHeight} leftMargin={leftMargin}>
      <ChartsSeriesColorWrapper>
        {!terminalDetailsLoaded || isRequestingTerminalHistory || !isNil(requestTerminalHistoryError) ? (
          <div className="ftv__charts__loading--container">
            {supportsFtv && !isNil(requestTerminalHistoryError) ? (
              <div className="error__container">
                <div className="error__message__container">
                  <Warning />
                  <div>Error Fetching Chart Data</div>
                  <Button color="primary" variant="text" onClick={() => fetchChartData(datapathUuid)}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <CircularProgress className="progress ftv__charts__loading" />
            )}
          </div>
        ) : supportsFtv ? (
          <ChartsList chartList={chartList} onChartListChange={setChartList}>
            {isExportView ? <Disclaimer /> : null}
          </ChartsList>
        ) : (
          <div className="ftv__charts__loading--container">
            <CircularProgress className="progress ftv__charts__loading" />
          </div>
        )}
      </ChartsSeriesColorWrapper>
      {/* Modal for Protocols Charts (Only displays if the relevant redux 
        fields are defined and Ui.allProtocolsModalVisible is set to true) */}
      <AllProtocolsModal />
    </ChartsContainerWrapper>
  );
}

const mapStateToProps = (state: RootState) => ({
  terminalDetails: state.terminals.tailDetailsPanelTerminal,
  selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
  isRequestingTerminalHistory: state.terminalHistory.isRequestingTerminalHistory,
  isTrafficEndpointEnabled: state.config.appConfig.isTrafficEndpointEnabled,
  requestTerminalHistoryError: state.terminalHistory.requestTerminalHistoryError,
  printMode: state.ui.printMode,
  mapSettingsFtvMapHeight: state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.mapSettingsFtvMapHeight,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    getTerminalHistoryOnce: (datapathUuid: string) =>
      TerminalHistoryActions.getTerminalHistoryOnce(datapathUuid)(dispatch),
  };
};
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(ChartsContainer);
